"use client";

import { useState, useEffect, useCallback } from "react";

const NAV_LINKS = [
  { label: "Templates", href: "/templates", sectionId: "templates" },
  { label: "How It Works", href: "/how-it-works", sectionId: "how-it-works" },
  { label: "Features", href: "#features", sectionId: "features" },
  { label: "Blog", href: "/blog", sectionId: "blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Track active section
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    NAV_LINKS.forEach((link) => {
      const el = document.getElementById(link.sectionId);
      if (!el) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(link.sectionId);
          }
        },
        { rootMargin: "-40% 0px -55% 0px" }
      );

      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleNavClick = useCallback((link: typeof NAV_LINKS[number]) => {
    setMenuOpen(false);
    // If on homepage, try scrolling to section first
    const el = document.getElementById(link.sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (!link.href.startsWith("#")) {
      // Navigate to the real page
      window.location.href = link.href;
    }
  }, []);

  return (
    <>
      <nav className={`iv-nav ${scrolled ? "iv-nav--scrolled" : ""}`}>
        <div className="iv-nav__inner">
          <a href="/" className="iv-nav__logo iv-font-display">
            MyShaadhi<span className="iv-nav__logo-accent">Link</span>
          </a>

          <ul className="iv-nav__links">
            {NAV_LINKS.map((link) => (
              <li key={link.sectionId}>
                <a
                  className={`iv-nav__link ${activeSection === link.sectionId ? "iv-nav__link--active" : ""}`}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link);
                  }}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>

          <a
            className="iv-nav__cta"
            href="#templates"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick(NAV_LINKS.find((l) => l.sectionId === "templates")!);
            }}
          >
            Get Started
          </a>

          <button
            className="iv-nav__menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`iv-mobile-menu ${menuOpen ? "iv-mobile-menu--open" : ""}`}>
        <span className="iv-mobile-menu__logo iv-font-display">
          MyShaadhi<span className="iv-mobile-menu__logo-accent">Link</span>
        </span>
        <button
          className="iv-mobile-menu__close"
          onClick={() => setMenuOpen(false)}
          aria-label="Close menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        {NAV_LINKS.map((link) => (
          <a
            key={link.sectionId}
            className="iv-mobile-menu__link"
            href={link.href}
            onClick={(e) => {
              e.preventDefault();
              handleNavClick(link);
            }}
          >
            {link.label}
          </a>
        ))}
        <a
          className="iv-btn iv-btn--primary"
          href="#templates"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick(NAV_LINKS.find((l) => l.sectionId === "templates")!);
          }}
        >
          Get Started
        </a>
      </div>
    </>
  );
}
